import { useSnapshot } from 'valtio';
import type { Address } from 'wagmi';

import { gql } from '@soundxyz/gql-string';
import { AuthUserDocument, useQuery } from '@graphql/reactQuery';

import { useIsHydrated } from '@hooks/useIsHydrated';
import { createContainer } from '../../utils/unstated';
import { authUserFilterQueryKey } from '../authHelpers';
import { graphqlClientHeaders } from '../headers';
import { authUserPersistenceStore, persistedUserState } from './persistence';

/*#__PURE__*/ gql(/* GraphQL */ `
  query authUser {
    authUser {
      id
      email
      verifiedNotificationEmail
      publicAddress
      lastReferralWithdrawableBalance
      webappUri
      username
      twitterHandle
      instagramHandle
      displayName
      description
      roles {
        isAdmin
        isArtistRelations
      }
      avatar {
        id
        url
        key
      }
      bannerImage {
        id
        url
        key
      }
      artist {
        id
        name
        webappUri
        soundHandle
        bannerImage {
          id
          url
          key
        }
      }
      creditAllocations {
        id
      }
      location {
        label
        placeId
      }
      invitedByUser {
        id
      }
      ...UserDashboardInfo
    }
  }
`);

const Auth = createContainer(function useAuth() {
  const headers = useSnapshot(graphqlClientHeaders);
  const isHydrated = useIsHydrated();
  const { isLoading: isPersistedUserStateLoading } = useSnapshot(persistedUserState);

  const authToken = headers['auth-token'];

  const hasAuthToken = !!authToken;

  const authFilterQueryKey = authUserFilterQueryKey({ authToken });

  const {
    data: authUserData,
    isInitialLoading: isConnectingAPIUserReactQuery,
    refetch: refetchUser,
  } = useQuery(AuthUserDocument, {
    enabled: hasAuthToken,
    /**
     * Regardless of user being connected or not.
     * If there is an authToken in local storage,
     * We will force try to get it from the backend
     *
     * The logic in this will handle the case where the user wallet is not connected.
     * Doing this will allow us to login users automatically and continue their old sessions.
     */
    fetchOptions: {
      headers,
    },
    filterQueryKey: authFilterQueryKey,
    keepPreviousData: hasAuthToken,
    onSuccess(data) {
      authUserPersistenceStore.set(data);
    },
    cacheTime: Infinity,
    /**
     * we should refetch the user across page reloads to make sure we have the latest data
     */
    staleTime: 0,
  });

  const isConnectingAPIUser = isPersistedUserStateLoading || isConnectingAPIUserReactQuery;

  const authUser = isHydrated ? authUserData?.data.authUser : null;

  const isAuthenticatedWithAPI = isHydrated && !!headers['auth-token'] && !!authUser;

  return {
    authUser,
    authUserAddress: authUser?.publicAddress as Address | undefined,
    isAuthenticatedWithAPI,
    isConnectingAPIUser,
    refetchUser,
    isHydrated,
    authFilterQueryKey,
  };
});

export const useAuth = () => Auth.useContainer();

export type AuthContainerValue = ReturnType<typeof useAuth>;

export const AuthContext = Auth.Context;

export const AuthProvider = Auth.Provider;
